import React, { useCallback, useEffect, useId, useState } from "react";
import { Box, CircularProgress, MenuItem, Select, Typography } from "@mui/material";
import IntegrationApi from "../../apis/integrationApi";
import { FONT, NEUTRAL, ORANGE, RADIUS, SELECTED_EDGE, VIOLET } from "../../styles/brandTokens";
import { RESOLVER_STATE_COLORS } from "../../shared/components/resolverStateStyle";
import type { AutoAgentMonitorActionSummary, AutoAgentMonitorSummary } from "../../types/integration";
import { ExecutionFilters, MONITOR_NONE, SUMMARY_WINDOWS } from "../theoAutomationsMonitorFilters";
import useVisiblePoll from "./useVisiblePoll";

const integrationApi = new IntegrationApi();
const TOP_ROWS = 5;

export const text = (size: number, color: string = NEUTRAL.text, weight = 400) => ({
  fontFamily: FONT.sans,
  fontSize: size,
  color,
  fontWeight: weight,
  lineHeight: 1.4,
});

export const cardSx = { background: NEUTRAL.surface, border: `1px solid ${NEUTRAL.border}`, borderRadius: `${RADIUS.card}px` };

const OUTCOMES = [
  { state: "success", label: "completed", color: RESOLVER_STATE_COLORS.success.dot, primary: true },
  { state: "waiting", label: "waiting", color: ORANGE.main, primary: true },
  { state: "running", label: "in progress", color: VIOLET.main, primary: false },
  { state: "cancelled", label: "cancelled", color: NEUTRAL.borderStrong, primary: false },
  { state: "declined", label: "not attempted", color: NEUTRAL.border, primary: false },
] as const;

export const formatCount = (value: number) => value.toLocaleString();

export function formatTimeSaved(minutes: number): string {
  if (minutes < 60) return `About ${Math.round(minutes)} minutes of technician time`;
  const hours = Math.round(minutes / 60);
  return `About ${formatCount(hours)} ${hours === 1 ? "hour" : "hours"} of technician time`;
}

const quietButtonSx = {
  border: 0,
  background: "transparent",
  font: "inherit",
  color: "inherit",
  cursor: "pointer",
  textAlign: "left" as const,
  borderRadius: `${RADIUS.control}px`,
  "&:focus-visible": { outline: "none", boxShadow: SELECTED_EDGE },
};

/** Mouse shortcut only: the legend below carries the same actions for keyboard and screen readers. */
const OutcomeBar: React.FC<{
  counts: Record<string, number>;
  total: number;
  onOpen: (state: string) => void;
}> = ({ counts, total, onOpen }) => (
  <Box sx={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", background: NEUTRAL.hover, gap: "2px" }}>
    {OUTCOMES.filter((outcome) => counts[outcome.state] > 0).map((outcome) => (
      <Box
        key={outcome.state}
        component="button"
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        title={`${formatCount(counts[outcome.state])} ${outcome.label}`}
        onClick={() => onOpen(outcome.state)}
        sx={{
          ...quietButtonSx,
          borderRadius: 0,
          p: 0,
          minWidth: 6,
          width: `${(counts[outcome.state] / total) * 100}%`,
          background: outcome.color,
          transition: "opacity 120ms ease",
          "&:hover": { opacity: 0.8 },
        }}
      />
    ))}
  </Box>
);

const OutcomeLegend: React.FC<{ counts: Record<string, number>; onOpen: (state: string) => void }> = ({ counts, onOpen }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, rowGap: 0.5, mt: 1.25, ml: -0.75 }}>
    {OUTCOMES.filter((outcome) => outcome.primary || counts[outcome.state] > 0).map((outcome) => (
      <Box
        key={outcome.state}
        component="button"
        type="button"
        onClick={() => onOpen(outcome.state)}
        sx={{ ...quietButtonSx, display: "inline-flex", alignItems: "center", gap: 0.75, px: 0.75, py: 0.5, "&:hover": { background: NEUTRAL.hover } }}
      >
        <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: "50%", background: outcome.color }} />
        <Box component="span" sx={text(13.5, outcome.primary ? NEUTRAL.text : NEUTRAL.textMuted, 600)}>
          {formatCount(counts[outcome.state] || 0)}
        </Box>
        <Box component="span" sx={text(13.5, outcome.primary ? NEUTRAL.textSecondary : NEUTRAL.textMuted)}>
          {outcome.label}
        </Box>
      </Box>
    ))}
  </Box>
);

const ActionsStat: React.FC<{ actions: AutoAgentMonitorActionSummary | null | undefined }> = ({ actions }) => (
  <Box>
    <Typography sx={{ ...text(30, NEUTRAL.text, 600), lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
      {actions ? formatCount(actions.total) : "—"}
    </Typography>
    <Typography sx={{ ...text(13.5, NEUTRAL.textSecondary), mt: 0.5 }}>actions taken</Typography>
    <Typography sx={{ ...text(12.5, actions ? NEUTRAL.textMuted : ORANGE.text), mt: 1 }}>
      {actions ? formatTimeSaved(actions.estimated_minutes) : "Couldn't load Theo's actions."}
    </Typography>
  </Box>
);

export interface TopRow {
  key: string;
  label: string;
  count: number;
  onClick?: () => void;
  /** Replaces the formatted count on the right, e.g. "26 h" when `count` is minutes. */
  valueLabel?: string;
  /** Muted context before the value, e.g. "328 actions". */
  detail?: string;
  /** Splits the bar by source, in order. Omitted, the bar is one violet run. */
  segments?: { key: string; count: number; color: string }[];
}

export const TopList: React.FC<{ title: string; rows: TopRow[]; emptyText: string; legend?: React.ReactNode }> = ({
  title,
  rows,
  emptyText,
  legend,
}) => {
  const max = Math.max(1, ...rows.map((row) => row.count));
  return (
    <Box component="section" aria-label={title} sx={{ ...cardSx, p: 2, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 1 }}>
        <Typography component="h3" sx={text(14, NEUTRAL.text, 600)}>
          {title}
        </Typography>
        {legend}
      </Box>
      {rows.length === 0 ? <Typography sx={text(13, NEUTRAL.textMuted)}>{emptyText}</Typography> : null}
      {rows.map((row) => {
        const content = (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Typography className="top-label" noWrap sx={{ ...text(13.5), transition: "color 120ms ease" }}>
                {row.label}
              </Typography>
              <Typography sx={{ ...text(13, NEUTRAL.textSecondary, 600), fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                {row.detail ? (
                  <Box component="span" sx={{ color: NEUTRAL.textMuted, fontWeight: 400, mr: 1 }}>
                    {row.detail}
                  </Box>
                ) : null}
                {row.valueLabel ?? formatCount(row.count)}
              </Typography>
            </Box>
            <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, background: NEUTRAL.hover }}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  borderRadius: 2,
                  overflow: "hidden",
                  background: VIOLET.main,
                  width: `${(row.count / max) * 100}%`,
                }}
              >
                {row.segments?.map((segment) =>
                  segment.count > 0 ? (
                    <Box
                      key={segment.key}
                      sx={{ height: "100%", background: segment.color, width: `${(segment.count / row.count) * 100}%` }}
                    />
                  ) : null
                )}
              </Box>
            </Box>
          </>
        );
        const rowSx = { display: "block", width: "calc(100% + 16px)", px: 1, py: 0.75, mx: -1 };
        return row.onClick ? (
          <Box
            key={row.key}
            component="button"
            type="button"
            onClick={row.onClick}
            sx={{ ...quietButtonSx, ...rowSx, "&:hover": { background: NEUTRAL.hover }, "&:hover .top-label": { color: VIOLET.main } }}
          >
            {content}
          </Box>
        ) : (
          <Box key={row.key} sx={rowSx}>
            {content}
          </Box>
        );
      })}
    </Box>
  );
};

export const windowSelectSx = {
  ...text(13.5),
  borderRadius: `${RADIUS.control}px`,
  "& .MuiSelect-select": { py: 0.6 },
  "& fieldset": { borderColor: NEUTRAL.borderStrong },
  "&:hover fieldset": { borderColor: `${NEUTRAL.textMuted} !important` },
  "&.Mui-focused fieldset": { borderColor: `${VIOLET.main} !important`, borderWidth: "1px !important" },
};

const SummaryPanel: React.FC<{
  agentId: number;
  refreshToken: number;
  onOpenExecutions: (patch: Partial<ExecutionFilters>) => void;
}> = ({ agentId, refreshToken, onOpenExecutions }) => {
  const windowLabelId = useId();
  const [windowValue, setWindowValue] = useState("all");
  const [summary, setSummary] = useState<AutoAgentMonitorSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * A background load is the live poll: no spinner, and a failure leaves the numbers already shown
   * (the error state would replace them) for the next tick to retry.
   */
  const loadSummary = useCallback(
    async (background = false) => {
      if (!background) {
        setLoading(true);
        setError(null);
      }
      try {
        setSummary(await integrationApi.getAutoAgentMonitorSummary(windowValue, agentId));
        setError(null);
      } catch (err: unknown) {
        if (!background) setError(err instanceof Error ? err.message : "Couldn't load the summary.");
      } finally {
        if (!background) setLoading(false);
      }
    },
    [agentId, windowValue]
  );

  useEffect(() => {
    void loadSummary();
  }, [loadSummary, refreshToken]);

  useVisiblePoll(() => loadSummary(true));

  const counts: Record<string, number> = Object.fromEntries(
    (summary?.by_execution_status || []).map((row) => [row.resolver_state || MONITOR_NONE, row.total])
  );
  const total = summary?.totals.total || 0;
  const openState = (state: string) => onOpenExecutions({ executionStatuses: [state] });

  const typeRows: TopRow[] = (summary?.by_ticket_type || [])
    .slice()
    .sort((a, b) => b.total - a.total)
    .slice(0, TOP_ROWS)
    .map((row) => ({
      key: row.ticket_type || MONITOR_NONE,
      label: row.ticket_type || "No ticket type",
      count: row.total,
      onClick: () => onOpenExecutions({ ticketTypes: [row.ticket_type || MONITOR_NONE] }),
    }));
  const actionRows: TopRow[] = (summary?.actions?.by_category || [])
    .slice(0, TOP_ROWS)
    .map((row) => ({ key: row.category, label: row.label, count: row.count }));

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box component="section" aria-labelledby="summary-headline" sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography component="label" id={windowLabelId} sx={text(12.5, NEUTRAL.textSecondary, 600)}>
              Showing
            </Typography>
            <Select
              size="small"
              labelId={windowLabelId}
              value={windowValue}
              onChange={(event) => setWindowValue(event.target.value)}
              sx={windowSelectSx}
            >
              {SUMMARY_WINDOWS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={text(13.5)}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {loading && summary ? <CircularProgress size={14} sx={{ color: VIOLET.main }} /> : null}
          </Box>
          {summary?.refreshed_at ? (
            <Typography sx={text(12, NEUTRAL.textMuted)}>
              Updated {new Date(summary.refreshed_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
            </Typography>
          ) : null}
        </Box>

        {error ? (
          <Typography role="alert" sx={text(13, ORANGE.text)}>
            {error}
          </Typography>
        ) : !summary ? (
          <CircularProgress size={22} sx={{ color: VIOLET.main }} />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 2fr) minmax(0, 1fr)" },
              gap: { xs: 2, md: 3 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography id="summary-headline" component="h2" sx={{ ...text(24, NEUTRAL.text, 600), lineHeight: 1.25, mb: 1.75 }}>
                {total === 0 ? (
                  "Theo hasn't worked any tickets in this window"
                ) : (
                  <>
                    Theo worked{" "}
                    <Box component="span" sx={{ color: VIOLET.theoText }}>
                      {formatCount(total)} {total === 1 ? "ticket" : "tickets"}
                    </Box>
                  </>
                )}
              </Typography>
              {total > 0 ? (
                <>
                  <OutcomeBar counts={counts} total={total} onOpen={openState} />
                  <OutcomeLegend counts={counts} onOpen={openState} />
                </>
              ) : null}
            </Box>
            <Box
              sx={{
                borderTop: { xs: `1px solid ${NEUTRAL.border}`, md: 0 },
                borderLeft: { xs: 0, md: `1px solid ${NEUTRAL.border}` },
                pt: { xs: 2, md: 0 },
                pl: { xs: 0, md: 3 },
                display: "flex",
                alignItems: "center",
              }}
            >
              <ActionsStat actions={summary.actions} />
            </Box>
          </Box>
        )}
      </Box>

      {summary ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
          <TopList title="Top ticket types" rows={typeRows} emptyText="No tickets in this window." />
          <TopList
            title="Top actions"
            rows={actionRows}
            emptyText={summary.actions ? "Theo hasn't taken any actions in this window." : "Couldn't load Theo's actions."}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default SummaryPanel;
